import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { format, subDays, parseISO } from 'date-fns';
import { nb } from 'date-fns/locale';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#0D1929', border: '1px solid rgba(59,111,212,0.4)', padding: '10px 14px', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
      <div style={{ color: '#3B6FD4', marginBottom: '4px' }}>{label}</div>
      <div style={{ color: '#EEF0F2' }}>{payload?.[0]?.value} kommentarer</div>
      {payload?.[1] && <div style={{ color: '#E76F51' }}>{payload?.[1]?.value} likes</div>}
    </div>
  );
};

const ActivityTimeline = ({ comments, loading }) => {
  const chartData = useMemo(() => {
    const days = 30;
    const buckets = {};
    for (let i = days - 1; i >= 0; i--) {
      const d = format(subDays(new Date(), i), 'dd.MM', { locale: nb });
      buckets[d] = { date: d, kommentarer: 0, likes: 0 };
    }
    comments?.forEach(c => {
      if (!c?.created_at) return;
      const d = format(parseISO(c?.created_at), 'dd.MM', { locale: nb });
      if (buckets?.[d]) {
        buckets[d].kommentarer += 1;
        buckets[d].likes += c?.likes_count || 0;
      }
    });
    return Object.values(buckets);
  }, [comments]);

  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', padding: '28px', marginBottom: '48px', position: 'relative', overflow: 'hidden' }}>
      {/* Diagonal cut accent */}
      <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', background: 'rgba(59,111,212,0.08)', clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', marginBottom: '24px' }}>
        <h2 style={{ fontFamily: 'Impact, sans-serif', fontSize: '1.6rem', color: '#EEF0F2', letterSpacing: '0.02em', transform: 'rotate(-0.5deg)' }}>AKTIVITETSTIDSLINJE</h2>
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: '#3B6FD4', letterSpacing: '0.15em' }}>SISTE 30 DAGER</span>
      </div>

      {loading ? (
        <div style={{ height: '200px', background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', color: 'rgba(238,240,242,0.3)' }}>LASTER DATA...</span>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorKomm" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B6FD4" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#3B6FD4" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorLikes" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#E76F51" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#E76F51" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', fill: 'rgba(238,240,242,0.35)' }} axisLine={false} tickLine={false} interval={4} />
            <YAxis tick={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', fill: 'rgba(238,240,242,0.35)' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="kommentarer" stroke="#3B6FD4" strokeWidth={2} fill="url(#colorKomm)" dot={false} />
            <Area type="monotone" dataKey="likes" stroke="#E76F51" strokeWidth={1.5} fill="url(#colorLikes)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      )}

      <div style={{ display: 'flex', gap: '20px', marginTop: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '20px', height: '2px', background: '#3B6FD4' }} />
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: 'rgba(238,240,242,0.5)' }}>Kommentarer</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '20px', height: '2px', background: '#E76F51' }} />
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: 'rgba(238,240,242,0.5)' }}>Likes</span>
        </div>
      </div>
    </div>
  );
};

export default ActivityTimeline;
